import React, { useState, useEffect, useRef } from 'react'
import { Modal, Button } from 'react-bootstrap'
import { QRCodeSVG } from 'qrcode.react'
import axios from 'axios'
import Header from '../../components/Header/index'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faClose, faRotateRight } from '@fortawesome/free-solid-svg-icons'

const QR_STATUS_URL =
  (process.env.REACT_APP_BASE_URL || 'https://app.yanibasimdakaratay.com') +
  '/api/hobi-bahce/qr-status'
const POLL_INTERVAL_MS = 2000

// v3.2: Tek QR token + TTL geri sayım. QR okutulup bariyer açılınca (used_at dolunca)
// onSuccess çağrılır — web ekranı kapatır. Süre dolunca "Yenile" butonu.
const BarrierQRModal = ({ open, qrPayload, expiresIn = 15, direction, onRefresh, onClose, onSuccess }) => {
  const [remaining, setRemaining] = useState(expiresIn)
  const [expired, setExpired] = useState(false)
  const [rejectionMessage, setRejectionMessage] = useState(null)
  const timerRef = useRef(null)
  const pollRef = useRef(null)
  const expiresAtRef = useRef(0)
  const lastRejectionAtRef = useRef(null)
  const rejectionTimeoutRef = useRef(null)

  // TTL geri sayım — timestamp tabanlı.
  // Ekran kilitliyken tarayıcı setInterval'i dondurur; timestamp ile telefon
  // açılınca gerçek kalan süre hesaplanır (kaçan tick'ler telafi edilir).
  useEffect(() => {
    if (!open || !qrPayload) return
    expiresAtRef.current = Date.now() + expiresIn * 1000
    setRemaining(expiresIn)
    setExpired(false)

    const tick = () => {
      const left = Math.max(0, Math.ceil((expiresAtRef.current - Date.now()) / 1000))
      setRemaining(left)
      if (left <= 0) {
        setExpired(true)
        clearInterval(timerRef.current)
      }
    }

    timerRef.current = setInterval(tick, 1000)
    // Sayfa tekrar görünür olunca anında doğru süreyi hesapla
    const onVisible = () => {
      if (document.visibilityState === 'visible') tick()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      clearInterval(timerRef.current)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [open, qrPayload, expiresIn])

  // Backend polling — QR okutulup bariyer açıldı mı (used_at) kontrol
  useEffect(() => {
    if (!open || !qrPayload) return
    lastRejectionAtRef.current = null
    setRejectionMessage(null)

    pollRef.current = setInterval(async () => {
      try {
        const res = await axios.post(QR_STATUS_URL, { qr_payload: qrPayload })
        const data = res.data?.data || res.data || {}
        if (data.used) {
          clearInterval(pollRef.current)
          if (typeof onSuccess === 'function') {
            onSuccess()
          }
          return
        }
        // Yeni rejection (yanlış kapı / süre / zaten kullanıldı) → banner göster
        if (data.last_rejection_at && data.last_rejection_at !== lastRejectionAtRef.current) {
          lastRejectionAtRef.current = data.last_rejection_at
          setRejectionMessage(data.last_rejection_reason || 'QR doğrulanamadı.')
          if (rejectionTimeoutRef.current) clearTimeout(rejectionTimeoutRef.current)
          rejectionTimeoutRef.current = setTimeout(() => setRejectionMessage(null), 6000)
        }
      } catch (err) {
        // poll hatası sessiz — TTL sayacı süreyi zaten yönetir
      }
    }, POLL_INTERVAL_MS)

    return () => {
      clearInterval(pollRef.current)
      if (rejectionTimeoutRef.current) clearTimeout(rejectionTimeoutRef.current)
    }
  }, [open, qrPayload, onSuccess])

  const directionLabel = direction === 'exit' ? 'Çıkış' : 'Giriş'

  return (
    <Modal
      show={open}
      onHide={onClose}
      style={{ userSelect: 'none' }}
      size='lg'
      centered
      fullscreen
      contentClassName={'border-radius-0'}
    >
      <Modal.Body>
        <Header onClick={onClose} />
        <div className='text-center mt-3 mb-2'>
          <h5 style={{ fontWeight: 700, color: '#205C42' }}>{directionLabel} — QR Kod</h5>
          <p style={{ fontSize: 14, marginTop: 6 }}>
            QR kodunu bariyer önündeki okuyucuya gösterin.
          </p>
        </div>

        <hr style={{ width: '25%', margin: '1.5rem auto', color: '#205C42' }} />

        {rejectionMessage && (
          <div
            style={{
              background: '#fdecea',
              border: '1px solid #e57373',
              color: '#a02020',
              padding: '0.7rem 0.9rem',
              borderRadius: 8,
              margin: '0 0 1rem 0',
              fontSize: 13,
              textAlign: 'center',
              fontWeight: 500,
            }}
          >
            {rejectionMessage}
          </div>
        )}

        <div
          className='p-4 rounded-3 d-flex justify-content-center align-items-center border'
          style={{ background: '#fff', minHeight: 240 }}
        >
          {qrPayload && !expired ? (
            <QRCodeSVG value={qrPayload} size={220} level='M' />
          ) : (
            <div style={{ textAlign: 'center', color: '#999' }}>
              <FontAwesomeIcon icon={faRotateRight} size='2x' />
              <p style={{ marginTop: 12, fontSize: 14 }}>QR kod süresi doldu</p>
            </div>
          )}
        </div>

        <div className='text-center mt-4'>
          {!expired ? (
            <p className='text-muted' style={{ fontSize: 14 }}>
              Kod <strong>{remaining}</strong> saniye geçerli.
            </p>
          ) : (
            <p style={{ fontSize: 14, color: '#C76A1E', fontWeight: 600 }}>
              Süre doldu — yeni kod alın.
            </p>
          )}
        </div>

        {expired && (
          <Button
            block
            onClick={onRefresh}
            style={{
              fontSize: '16px',
              backgroundColor: '#205C42',
              border: 'none',
              borderRadius: '5rem',
              color: '#FFFFFF',
              padding: '.7rem .5rem',
              margin: '0.5rem 0 0.5rem 0',
              width: '100%',
            }}
          >
            <FontAwesomeIcon icon={faRotateRight} /> QR Kodu Yenile
          </Button>
        )}

        <Button
          block
          onClick={onClose}
          style={{
            fontSize: '16px',
            backgroundColor: expired ? '#888' : '#205C42',
            border: 'none',
            borderRadius: '5rem',
            color: '#FFFFFF',
            padding: '.7rem .5rem',
            margin: '0.6rem 0 3rem 0',
            width: '100%',
          }}
        >
          <FontAwesomeIcon icon={faClose} size='lg' /> Kapat
        </Button>
      </Modal.Body>
    </Modal>
  )
}

export default BarrierQRModal
